import { useState } from 'react'
import { Box, ScrollArea, Text, UnstyledButton } from '@mantine/core'

const QUICK = ['👍', '❤️', '😂', '😮', '😢', '🙏']

const CATEGORIES: { name: string; emojis: string[] }[] = [
  {
    name: 'Reações',
    emojis: ['👍', '👎', '❤️', '🔥', '🎉', '👏', '🙏', '💪', '✅', '❌', '👀', '🚀'],
  },
  {
    name: 'Rostos',
    emojis: ['😀', '😂', '🙂', '😉', '😊', '😍', '😎', '🤔', '😅', '😭', '😡', '😱', '🥳', '😴', '🤯', '😬'],
  },
  {
    name: 'Mãos',
    emojis: ['👋', '🤝', '✌️', '🤞', '👌', '🤙', '💯', '🙌', '👊', '✋'],
  },
  {
    name: 'Objetos',
    emojis: ['💡', '📌', '📎', '⚠️', '🔒', '🔑', '📈', '📉', '🐛', '⏰', '💸', '🟢', '🔴', '🟡'],
  },
]

const emojiButtonStyle = {
  fontSize: 20,
  lineHeight: 1,
  padding: '4px 6px',
  borderRadius: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'background 0.1s ease',
}

interface EmojiPickerProps {
  onPick: (emoji: string) => void
  onClose: () => void
  /**
   * Quando true, o root usa position:relative (o PAI posiciona/ancorá — ex.: o
   * Composer abre o picker PARA CIMA, já que fica no rodapé da tela). Default
   * false = position:absolute (comportamento das reações no balão). */
  inline?: boolean
}

export function EmojiPicker({ onPick, onClose, inline = false }: EmojiPickerProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box
      aria-label="Seletor de emojis"
      onMouseLeave={onClose}
      style={{
        position: inline ? 'relative' : 'absolute',
        zIndex: 300,
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 10,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        padding: '8px 10px',
        minWidth: 240,
      }}
    >
      {/* Barra rápida */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {QUICK.map((e) => (
          <UnstyledButton
            key={e}
            onClick={() => onPick(e)}
            aria-label={`Reagir ${e}`}
            style={emojiButtonStyle}
            onMouseEnter={(ev) => {
              ;(ev.currentTarget as HTMLButtonElement).style.background =
                'var(--mantine-color-default-hover)'
            }}
            onMouseLeave={(ev) => {
              ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
            }}
          >
            {e}
          </UnstyledButton>
        ))}
        <UnstyledButton
          onClick={() => setExpanded((v) => !v)}
          aria-label="Mais emojis"
          aria-expanded={expanded}
          style={{
            ...emojiButtonStyle,
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--mantine-color-dimmed)',
            padding: '4px 8px',
          }}
          onMouseEnter={(ev) => {
            ;(ev.currentTarget as HTMLButtonElement).style.background =
              'var(--mantine-color-default-hover)'
          }}
          onMouseLeave={(ev) => {
            ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
          }}
        >
          ＋
        </UnstyledButton>
      </Box>

      {/* Grade expandida por categoria */}
      {expanded && (
        <ScrollArea.Autosize mah={260} mt={8} scrollbarSize={4}>
          <Box style={{ paddingRight: 4 }}>
            {CATEGORIES.map((cat) => (
              <Box key={cat.name} mb={8}>
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  style={{ marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  {cat.name}
                </Text>
                <Box
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  {cat.emojis.map((e) => (
                    <UnstyledButton
                      key={e}
                      onClick={() => onPick(e)}
                      aria-label={`Reagir ${e}`}
                      style={emojiButtonStyle}
                      onMouseEnter={(ev) => {
                        ;(ev.currentTarget as HTMLButtonElement).style.background =
                          'var(--mantine-color-default-hover)'
                      }}
                      onMouseLeave={(ev) => {
                        ;(ev.currentTarget as HTMLButtonElement).style.background = 'transparent'
                      }}
                    >
                      {e}
                    </UnstyledButton>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </ScrollArea.Autosize>
      )}
    </Box>
  )
}
